import React, { useCallback, useEffect, useState } from 'react';
import { RefreshCw, Filter, FileText } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import { formatDateTime } from '../../utils/datetime';

const ACTION_FILTERS = [
  { value: '', label: 'All actions' },
  { value: 'platform.', label: 'Platform actions' },
  { value: 'platform.org.', label: 'Org lifecycle' },
  { value: 'platform.user.', label: 'Platform users' },
  { value: 'platform.impersonate', label: 'Impersonation' },
];

const ACTION_STYLES = {
  'platform.impersonate.start': 'bg-blue-50 text-blue-700',
  'platform.impersonate.exit': 'bg-slate-100 text-slate-600',
  'platform.org.suspended': 'bg-amber-50 text-amber-700',
  'platform.org.reactivated': 'bg-emerald-50 text-emerald-700',
  'platform.org.plan_changed': 'bg-purple-50 text-purple-700',
  'platform.org.deleted': 'bg-rose-50 text-rose-700',
  'platform.user.created': 'bg-emerald-50 text-emerald-700',
  'platform.user.updated': 'bg-blue-50 text-blue-700',
  'platform.user.deleted': 'bg-rose-50 text-rose-700',
};

const AuditPanel = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionPrefix, setActionPrefix] = useState('platform.');
  const [orgFilter, setOrgFilter] = useState('');
  const [expanded, setExpanded] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (actionPrefix) params.set('action_prefix', actionPrefix);
      if (orgFilter.trim()) params.set('organization_id', orgFilter.trim());
      params.set('limit', '200');
      const res = await apiClient.get(`/api/platform/audit-logs?${params.toString()}`);
      setEntries(res.data.entries || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  }, [actionPrefix, orgFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[15px] font-semibold text-slate-900 dark:text-slate-100">Audit log</h2>
          <p className="text-[12px] text-slate-500 dark:text-slate-400">Every platform action, stamped with the real actor.</p>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="flex items-center gap-1.5 px-3 py-1.5 text-[13px] text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-50 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 p-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            <Filter className="h-3 w-3 inline mr-1" />
            Action
          </label>
          <select
            value={actionPrefix}
            onChange={(e) => setActionPrefix(e.target.value)}
            className="px-3 py-1.5 text-[13px] bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          >
            {ACTION_FILTERS.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
          </select>
        </div>
        <div className="flex-1 min-w-[200px]">
          <label className="block text-[11px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
            Organization id
          </label>
          <input
            type="text"
            value={orgFilter}
            onChange={(e) => setOrgFilter(e.target.value)}
            placeholder="Filter by target organization id…"
            className="w-full px-3 py-1.5 text-[13px] bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-[13px] rounded-lg px-4 py-3">{error}</div>
      )}

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200/80 dark:border-slate-800 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-[13px] text-slate-500">Loading…</div>
        ) : entries.length === 0 ? (
          <div className="p-8 text-center text-[13px] text-slate-500">
            <FileText className="h-8 w-8 text-slate-300 mx-auto mb-2" />
            No entries match these filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead className="bg-slate-50 dark:bg-slate-800/50 text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold">When</th>
                  <th className="px-4 py-2 text-left font-semibold">Actor</th>
                  <th className="px-4 py-2 text-left font-semibold">Action</th>
                  <th className="px-4 py-2 text-left font-semibold">Target</th>
                  <th className="px-4 py-2 text-left font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {entries.map((e) => {
                  const target = e.target_organization_name || e.target_user_email || e.target_organization_id || e.target_user_id || '—';
                  const actionStyle = ACTION_STYLES[e.action] || 'bg-slate-100 text-slate-600';
                  const isOpen = expanded === e._id;
                  return (
                    <React.Fragment key={e._id}>
                      <tr className="hover:bg-slate-50/60 dark:hover:bg-slate-800/40 cursor-pointer" onClick={() => setExpanded(isOpen ? null : e._id)}>
                        <td className="px-4 py-2.5 text-slate-500 dark:text-slate-400 text-[11px]">{formatDateTime(e.timestamp) || '—'}</td>
                        <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">
                          {e.actor_email}
                          {e.actor_platform_role && (
                            <span className="ml-1.5 text-[10px] text-slate-400 dark:text-slate-500">{e.actor_platform_role}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`inline-block px-1.5 py-0.5 text-[10px] rounded font-medium ${actionStyle}`}>
                            {e.action}
                          </span>
                        </td>
                        <td className="px-4 py-2.5 text-slate-700 dark:text-slate-300">{target}</td>
                        <td className="px-4 py-2.5 text-slate-400 dark:text-slate-500 text-[11px]">
                          {isOpen ? 'Hide' : 'Expand'}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={5} className="px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200/60 dark:border-slate-800">
                            <pre className="text-[11px] text-slate-600 dark:text-slate-300 overflow-x-auto">
                              {JSON.stringify(e, null, 2)}
                            </pre>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditPanel;
